import React, { useState, useEffect, useRef, useMemo } from 'react';
import MonacoEditor from '@monaco-editor/react';
import { useParams, useNavigate } from 'react-router-dom'; 
import { useTranslation } from 'react-i18next';
import { Layout, Tree, Button, Select, Tabs, Input, Space, Divider, Card, Tag, message, Tooltip, Empty, Row, Col, Spin, Checkbox } from 'antd';
import { 
    SaveOutlined, EyeOutlined, PlusOutlined, DeleteOutlined, 
    PartitionOutlined, QrcodeOutlined, BarChartOutlined, 
    FileTextOutlined, CodeOutlined, SettingOutlined, ScissorOutlined,
    TableOutlined, ArrowLeftOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Content, Sider, Header } = Layout;
const { TabPane } = Tabs;

export default function ReportDesigner() {
    const { t, i18n } = useTranslation();
    const { id } = useParams(); 
    const navigate = useNavigate();
    const editorRef = useRef(null);
    const [activeTab, setActiveTab] = useState('body_html');
    const [loading, setLoading] = useState(false);
    
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    // 1. خريطة البيانات المتقدمة - كاملة 100% كما هي في نسختك الأصلية
    const MODEL_FIELDS = useMemo(() => ({
        WORKORDER: [
            { 
                title: t('reports.designer.master_info'), 
                key: 'wo_header', 
                children: [
                    { title: 'WO Number', key: 'main_wonum', content: '<div class="f-item"><b>Order #:</b> {{ main.wonum }}</div>', isLeaf: true },
                    { title: 'Description', key: 'main_desc', content: '<div class="f-item"><b>Description:</b> {{ main.description }}</div>', isLeaf: true },
                    { title: 'Status', key: 'main_status', content: '<span class="status-badge">{{ main.status }}</span>', isLeaf: true },
                ]
            },
            { 
                title: t('reports.designer.relational_data'), 
                key: 'wo_rel', 
                children: [
                    { title: 'Asset Name', key: 'asset_name', content: '{{ asset.description }}', isLeaf: true },
                    { title: 'Location ID', key: 'loc_id', content: '{{ location.location_id }}', isLeaf: true },
                ]
            },
            { 
                title: t('reports.designer.detail_tables'), 
                key: 'wo_tables', 
                children: [
                    { 
                        title: 'Tasks Table', 
                        key: 'tasks_table', 
                        content: `
                        <table class="rpt-table">
                            <thead>
                                <tr><th>Seq</th><th>Task Description</th><th>Status</th></tr>
                            </thead>
                            <tbody>
                                {% for t in tasks %}
                                <tr><td>{{ t.wosequence }}</td><td>{{ t.description }}</td><td>{{ t.status }}</td></tr>
                                {% endfor %}
                            </tbody>
                        </table>`, 
                        isLeaf: true 
                    },
                    { 
                        title: 'Planned Materials', 
                        key: 'mat_table', 
                        content: `
<table class="rpt-table">
    <thead>
        <tr><th>Item</th><th>Qty</th><th>Unit Cost</th></tr>
    </thead>
    <tbody>
        {% for m in planned_materials %}
        <tr><td>{{ m.itemnum }}</td><td>{{ m.quantity }}</td><td>{{ m.unitcost }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, 
                        isLeaf: true 
                    }
                ]
            }
        ],
        ASSET: [
            { title: t('reports.designer.fields.asset_basic'), key: 'as_basic', children: [
                { title: 'Asset Num', key: '{{ main.assetnum }}', isLeaf: true },
                { title: 'Install Date', key: '{{ main.install_date }}', isLeaf: true },
            ]}
        ],
        INVENTORY: [
            { title: t('reports.designer.fields.inv_basic'), key: 'inv_basic', children: [
                { title: 'Item Num', key: '{{ main.itemnum }}', isLeaf: true },
                { title: 'Storeroom', key: '{{ main.location }}', isLeaf: true },
                { title: 'Site', key: '{{ main.siteid }}', isLeaf: true },
                { title: 'Org', key: '{{ main.orgid }}', isLeaf: true },
                { title: 'Current Stock', key: '{{ main.curbal }}', isLeaf: true },
            ]}
        ],
        VENDOR: [
            { title: t('reports.designer.fields.vend_basic'), key: 'vend_basic', children: [
                { title: 'Comp Num', key: '{{ main.comp_num }}', isLeaf: true},
                { title: 'Name', key: '{{ main.name }}', isLeaf: true},
            ]}
        ],
        RFQ: [
            { title: t('reports.designer.fields.rfq_basic'), key: 'rfq_basic', children: [
                { title: 'RFQ Num', key: '{{ main.rfqnum }}', isLeaf: true},
                { title: 'Description', key: '{{ main.description }}', isLeaf: true},
            ]}
        ]
    }), [t]);

    // 🎯 الحالة الشاملة للتقرير
    const [report, setReport] = useState({
        name: "Professional Work Order Report",
        ref_object: "WORKORDER",
        header_html: "<div class='report-header'>\n  <h2>TEKNORA ASSET MANAGEMENT</h2>\n  <p>Date: {{ print_date }}</p>\n</div>",
        body_html: "<div class='report-body'>\n  \n</div>",
        footer_html: "<div class='report-footer'>\n  <p>Page <span class='pageNumber'></span></p>\n</div>",
        custom_css: ".rpt-table { width:100%; border-collapse:collapse; } \n.rpt-table th { background:#005495; color:#fff; padding:8px; } \n.rpt-table td { border:1px solid #ddd; padding:8px; }",
        parameters_config: [] 
    });

    useEffect(() => {
        if (id && id !== 'new') {
            setLoading(true);
            axios.get(`/api/reports/template-detail/${id}`)
                .then(res => {
                    const data = res.data;
                    setReport({
                        ...data,
                        parameters_config: data.parameters_config || []
                    });
                })
                .catch(err => {
                    message.error(t('reports.toasts.load_fail') + err.message);
                })
                .finally(() => setLoading(false));
        } else {
            setReport({
                name: "New Report Template",
                ref_object: "WORKORDER",
                header_html: "<h2>Report Header</h2>",
                body_html: "",
                footer_html: "<p>Page footer</p>",
                custom_css: ".rpt-table { width: 100%; }",
                parameters_config: []
            });
        }
    }, [id, t]);

    const injectCode = (content) => {
        if (editorRef.current) {
            const editor = editorRef.current;
            const selection = editor.getSelection();
            editor.executeEdits("maxeam-designer", [{ 
                range: selection, 
                text: content, 
                forceMoveMarkers: true 
            }]);
        } else {
            message.warning(t('studio.console.init'));
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const res = await axios.post('/api/reports/report-templates/save', report);
            message.success(t('reports.toasts.save_success'));
            if (!id || id === 'new') {
                navigate(`/reportdesigner/${res.data.id}`);
            }
        } catch (err) {
            message.error(t('reports.toasts.save_fail') + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = () => {
        const previewData = {
            ...report,
            is_preview: true,
            ref_id: null
        };
        axios.post('/api/reports/preview-session', previewData).then(res => {
            window.open(`/api/reports/preview?session_id=${res.data.session_id}&preview=true`, '_blank');
        });
    };

    const addParam = () => {
        const config = report.parameters_config || [];
        const newParam = { name: `p${config.length + 1}`, label: 'New Parameter', type: 'text' };
        setReport({...report, parameters_config: [...config, newParam]});
    };

    const updateParam = (index, field, value) => {
        const updated = [...(report.parameters_config || [])];
        updated[index][field] = value;
        setReport({...report, parameters_config: updated});
    };

    const deleteParam = (index) => {
        const updated = (report.parameters_config || []).filter((_, i) => i !== index);
        setReport({...report, parameters_config: updated});
    };

    if (loading) return <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spin size="large" tip={t('workflow.loading')} /></div>;

    return (
        <Layout style={{ height: '100vh', background: '#fff' }} className={isRTL ? 'font-cairo' : 'font-sans'} dir={isRTL ? 'rtl' : 'ltr'}>
            <Header style={{ background: '#001529', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 20px' }}>
                <Space>
                    <Button ghost icon={<ArrowLeftOutlined className={isRTL ? 'rotate-180' : ''} />} onClick={() => navigate('/reportlist')}>{t('common.back')}</Button>
                    <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>
                        <SettingOutlined /> {t('reports.designer.title')}
                    </div>
                </Space>
                <Space>
                    <Button icon={<EyeOutlined />} onClick={handlePreview}>{t('reports.designer.live_preview')}</Button>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>{t('reports.designer.publish')}</Button>
                </Space>
            </Header>

            <Layout className="overflow-hidden">
                <Sider width={320} theme="light" style={{ borderRight: isRTL ? 'none' : '1px solid #f0f0f0', borderLeft: isRTL ? '1px solid #f0f0f0' : 'none', overflowY: 'auto', padding: '15px' }}>
                    <h4 className="font-bold text-sm mb-2">{t('reports.designer.model_select')}</h4>
                    <Select style={{ width: '100%' }} value={report.ref_object} onChange={v => setReport({...report, ref_object: v})}>
                        <Select.Option value="WORKORDER">Work Order Hub</Select.Option>
                        <Select.Option value="ASSET">Asset Registry</Select.Option>
                        <Select.Option value="INVENTORY">Inventory Snapshot</Select.Option>
                        <Select.Option value="VENDOR">Vendor</Select.Option>
                        <Select.Option value="RFQ">RFQ</Select.Option>
                    </Select>

                    <Divider className="my-4" />
                    
                    <h4 className="font-bold text-sm mb-2"><PartitionOutlined /> {t('reports.designer.data_explorer')}</h4>
                    <Tree
                        showLine
                        treeData={MODEL_FIELDS[report.ref_object] || []}
                        onSelect={(keys, info) => { if(info.node.isLeaf) injectCode(info.node.content) }}
                    />

                    <Divider className="my-4" />

                    <h4 className="font-bold text-sm mb-2"><ScissorOutlined /> {t('reports.designer.components')}</h4>
                    <div className="grid grid-cols-2 gap-2">
                        <Button size="small" icon={<QrcodeOutlined />} onClick={() => injectCode("<qr-code data='{{ main.wonum }}' size='100'></qr-code>")}>QR Code</Button>
                        <Button size="small" icon={<TableOutlined />} onClick={() => injectCode("<div style='page-break-after: always;'></div>")}>{t('reports.designer.page_break')}</Button>
                    </div>
                    
                    <Divider className="my-4" />
                    
                    <div style={{ marginBottom: 15, padding: '10px', background: '#f9f9f9', borderRadius: '4px' }}>
                        <Checkbox 
                            checked={report.is_summary} 
                            onChange={e => setReport({...report, is_summary: e.target.checked})}
                        >
                            <b>{t('reports.designer.is_summary')}</b>
                        </Checkbox>
                    </div>

                    <Divider className="my-4" />

                    <h4 className="font-bold text-sm mb-2">{t('reports.designer.user_params')}</h4>
                    {(report.parameters_config || []).map((p, idx) => (
                        <Card size="small" key={idx} className="mb-2 bg-gray-50" 
                            extra={<DeleteOutlined className="text-red-500 cursor-pointer" onClick={() => deleteParam(idx)} />}>
                            <Input size="small" placeholder={t('field_config.field_name')} value={p.name} onChange={e => updateParam(idx, 'name', e.target.value)} />
                            <Input size="small" placeholder={t('workflow.node_label')} value={p.label} className="mt-1" onChange={e => updateParam(idx, 'label', e.target.value)} />
                            <Select size="small" className="w-full mt-1" value={p.type} onChange={v => updateParam(idx, 'type', v)}>
                                <Select.Option value="text">Text Input</Select.Option>
                                <Select.Option value="dropdown">Lookup (Search)</Select.Option>
                                <Select.Option value="domain">DOMAIN</Select.Option>
                                <Select.Option value="date">Date Picker</Select.Option>
                            </Select>

                            {p.type === 'dropdown' && (
                                <Select size="small" placeholder="Select Lookup Module" className="w-full mt-1" value={p.lookupType} onChange={v => updateParam(idx, 'lookupType', v)}>
                                    <Select.Option value="sites">Sites</Select.Option>
                                    <Select.Option value="storerooms">Storerooms</Select.Option>
                                    <Select.Option value="assets">Assets</Select.Option>
                                    <Select.Option value="items">Item Master</Select.Option>
                                    <Select.Option value="workorder">Work Orders</Select.Option>
                                    <Select.Option value="companies">Vendors</Select.Option>
                                </Select>
                            )}
                            {p.type === 'domain' && (
                                <Select size="small" placeholder="Select Domain" className="w-full mt-1" value={p.lookupType} onChange={v => updateParam(idx, 'lookupType', v)}>
                                    <Select.Option value="assettype">ASSET TYPE</Select.Option>
                                    <Select.Option value="country">COUNTRY</Select.Option>
                                    <Select.Option value="metertype">METER TYPE</Select.Option>
                                    <Select.Option value="loctype">LOCATION TYPE</Select.Option>
                                    <Select.Option value="wotype">WORKORDER TYPE</Select.Option>
                                    <Select.Option value="wostatus">WORKORDER STATUS</Select.Option>
                                    <Select.Option value="assetstatus">ASSET STATUS</Select.Option>
                                    <Select.Option value="uom">UNIT OF MISURES</Select.Option>
                                </Select>
                            )}
                        </Card>
                    ))}
                    <Button type="dashed" block icon={<PlusOutlined />} onClick={addParam} className="mt-2">{t('reports.designer.add_param')}</Button>
                </Sider>

                <Content style={{ display: 'flex', flexDirection: 'column' }} className="bg-[#0b0e14]">
                    <div className="p-4 bg-white border-b flex items-center">
                        <Input variant="borderless" value={report.name} onChange={e => setReport({...report, name: e.target.value})} style={{ fontWeight: 'bold', fontSize: '18px' }} />
                    </div>

                    <Tabs activeKey={activeTab} onChange={setActiveTab} className="px-4 bg-white shrink-0"
                        items={[
                            { label: t('reports.designer.tabs.body'), key: 'body_html', icon: <CodeOutlined /> },
                            { label: t('reports.designer.tabs.header'), key: 'header_html', icon: <FileTextOutlined /> },
                            { label: t('reports.designer.tabs.footer'), key: 'footer_html', icon: <FileTextOutlined /> },
                            { label: t('reports.designer.tabs.css'), key: 'custom_css', icon: <SettingOutlined /> },
                        ]} 
                    />

                    <div style={{ flex: 1 }} dir="ltr">
                        <MonacoEditor
                            height="100%"
                            language={activeTab === 'custom_css' ? 'css' : 'html'}
                            theme="vs-dark"
                            value={report[activeTab] || ""}
                            onMount={(editor) => { editorRef.current = editor; }}
                            onChange={(val) => setReport({...report, [activeTab]: val})}
                            options={{ fontSize: 14, minimap: { enabled: false }, automaticLayout: true }}
                        />
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
}